/**
 * useExportOAuth — handles OAuth connection flow for export providers.
 *
 * Manages:
 * - Checking if a provider is connected
 * - Loading provider-specific data (Jira resources/projects, Linear teams)
 * - Initiating OAuth flow and polling for completion
 * - Cleanup on unmount
 */
import { useEffect, useRef, useCallback } from 'react';
import { expectArray } from '../../../apiShape.js';
import { toUserFacingMessage } from '../../../userFacingError.js';
export function useExportOAuth(runtime, { isOpen, isAnonymous, provider, setExportState, setError, onReconnectNeeded, onSyncReconnectNeeded, onJiraDataLoaded, onLinearDataLoaded, onNotionConnected, onAsanaDataLoaded, onTrelloDataLoaded, onClickUpDataLoaded, onBasecampDataLoaded, onMondayDataLoaded, onGitHubDataLoaded, }) {
    const { api: integrationsApi, debug } = runtime;
    const pollIntervalRef = useRef(null);
    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            debug.log('Stopping OAuth poll');
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [runtime]);
    /** Load provider data — reusable after OAuth completes */
    const loadProviderData = useCallback(async () => {
        debug.log('Loading data for provider:', provider);
        setExportState('loading');
        setError(null);
        onReconnectNeeded?.(false);
        try {
            const integrations = await integrationsApi.list();
            debug.log('Loaded integrations:', integrations.length);
            const connected = integrations.find(i => i.provider === provider && i.connected !== false);
            onSyncReconnectNeeded?.(connected?.syncReconnectRequired === true);
            if (!connected) {
                debug.log('Not connected to provider:', provider);
                setExportState('not-connected');
                return false;
            }
            if (provider === 'jira') {
                // A 200 with the wrong body shape ({} instead of { resources,
                // projects }) used to throw a raw TypeError here that rendered
                // verbatim in the modal (error-path EP-202, the EP-186 false-pass
                // mechanism). Validate at the boundary so it becomes a handled,
                // user-facing error.
                const jiraData = (await integrationsApi.getJiraProjects());
                const resources = expectArray(jiraData?.resources, 'Jira sites');
                const projects = expectArray(jiraData?.projects, 'Jira projects');
                debug.log('Loaded Jira data:', { resources: resources.length, projects: projects.length });
                onJiraDataLoaded?.({ resources, projects });
            }
            else if (provider === 'linear') {
                const teams = expectArray(await integrationsApi.getLinearTeams(), 'Linear teams');
                debug.log('Loaded Linear teams:', teams.length);
                onLinearDataLoaded?.(teams);
            }
            else if (provider === 'asana') {
                const workspaces = expectArray(await integrationsApi.getAsanaWorkspaces(), 'Asana workspaces');
                debug.log('Loaded Asana workspaces:', workspaces.length);
                onAsanaDataLoaded?.(workspaces);
            }
            else if (provider === 'github') {
                onGitHubDataLoaded?.(await integrationsApi.getGitHubRepositories());
            }
            else if (provider === 'clickup') {
                onClickUpDataLoaded?.(expectArray(await integrationsApi.getClickUpWorkspaces(), 'ClickUp workspaces'));
            }
            else if (provider === 'basecamp') {
                onBasecampDataLoaded?.(expectArray(await integrationsApi.getBasecampProjects(), 'Basecamp projects'));
            }
            else if (provider === 'monday') {
                onMondayDataLoaded?.(expectArray(await integrationsApi.getMondayBoards(), 'monday.com boards'));
            }
            else if (provider === 'trello') {
                const boards = expectArray(await integrationsApi.getTrelloBoards(), 'Trello boards');
                debug.log('Loaded Trello boards:', boards.length);
                onTrelloDataLoaded?.(boards);
            }
            else {
                // Notion: no provider-specific data to load. The databaseId rides on
                // the same providerMetadata object (server persists it after the DB is
                // auto-created); thread it through so the schema picker can resolve it.
                debug.log('Notion connected, skipping data load');
                const wsName = connected.providerMetadata?.workspaceName ?? null;
                onNotionConnected?.(wsName, (connected.providerMetadata?.databaseId ?? null));
            }
            setExportState('select');
            return true;
        }
        catch (err) {
            debug.error('Error loading data:', err);
            // The server returns NEEDS_RECONNECT (401) when the stored token is
            // rejected; surface it so the modal can offer a reconnect action instead
            // of a dead-end error. fetchApi threads the server error `code`.
            const code = err && typeof err === 'object' && 'code' in err ? err.code : undefined;
            if (code === 'NEEDS_RECONNECT') {
                onReconnectNeeded?.(true);
            }
            setError(toUserFacingMessage(err, 'Failed to load data'));
            setExportState('error');
            return false;
        }
    }, [provider, setExportState, setError, onReconnectNeeded, onSyncReconnectNeeded, onJiraDataLoaded, onLinearDataLoaded, onNotionConnected, onAsanaDataLoaded, onTrelloDataLoaded, onClickUpDataLoaded, onBasecampDataLoaded, onMondayDataLoaded, onGitHubDataLoaded, runtime]);
    /** Initiate OAuth flow — opens auth page, polls for connection */
    const handleConnect = useCallback(async () => {
        debug.log('Initiating OAuth for:', provider);
        setExportState('connecting');
        setError(null);
        onReconnectNeeded?.(false);
        try {
            const { url } = await integrationsApi.initiateAuth(provider);
            debug.log('Opening OAuth URL');
            window.open(url, '_blank', 'noopener');
            let attempts = 0;
            const maxAttempts = 60;
            pollIntervalRef.current = setInterval(async () => {
                attempts++;
                debug.log('Polling for connection, attempt:', attempts);
                try {
                    const integrations = await integrationsApi.list();
                    // A reconnect is done when the NEW authorization lands; the old row
                    // is already there and still flagged until then.
                    const connected = integrations.find(i => i.provider === provider && i.connected !== false && !i.syncReconnectRequired);
                    if (connected) {
                        debug.log('Connection detected!');
                        stopPolling();
                        await loadProviderData();
                    }
                    else if (attempts >= maxAttempts) {
                        debug.log('Polling timed out');
                        stopPolling();
                        // Re-read rather than assume: a user who abandoned a reconnect is
                        // still connected and can export (loadProviderData picks the state).
                        await loadProviderData();
                    }
                }
                catch {
                    // Ignore transient poll errors
                }
            }, 2000);
        }
        catch (err) {
            debug.error('OAuth initiation failed:', err);
            setError(toUserFacingMessage(err, 'Failed to start connection'));
            setExportState('not-connected');
        }
    }, [provider, loadProviderData, stopPolling, setExportState, setError, onReconnectNeeded, runtime]);
    // Load data when modal opens (skip for anonymous users — ExportModal handles auth gate)
    useEffect(() => {
        if (!isOpen || isAnonymous)
            return;
        debug.log('Modal opened, loading provider data');
        loadProviderData();
        return stopPolling;
    }, [isOpen, isAnonymous, loadProviderData, stopPolling]);
    // Stop stale polling when provider changes mid-flow
    useEffect(() => {
        debug.log('Provider changed to:', provider, '— clearing any active poll');
        stopPolling();
    }, [provider, stopPolling]);
    return { handleConnect, stopPolling, reloadProviderData: loadProviderData };
}
//# sourceMappingURL=useExportOAuth.js.map